import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AdminLayout from '../../components/AdminLayout';
import api from '../../services/api';

import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  X,
  FileText,
  Image,
  BarChart3,
  Search,
  Sparkles
} from 'lucide-react';

const PAGE_SIZE = 10;

const entryToolbarPrimaryClass =
  'inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 text-sm font-medium text-white transition-colors hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-teal-800 disabled:opacity-50';
const entryToolbarSecondaryClass =
  'inline-flex h-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2';

function parseJsonIfString(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
}

const VENUE_FIELD = 'store_or_venue_name_of_purchase';

/** Store from receipts.validation_data (metadata.store); then form store_name; if still empty, form venue field. */
function getReceiptStoreFromEntry(entry) {
  if (!entry) return { name: null, address: null };
  const vd = parseJsonIfString(entry.receipt_validation_data) || entry.receipt_validation_data;
  const meta = entry.receipt_metadata || vd?.metadata;
  const store = meta?.store || vd?.store;
  const formData = parseJsonIfString(entry.form_data) || (typeof entry.form_data === 'object' && entry.form_data) || {};
  const dynamicData =
    parseJsonIfString(entry.dynamic_form_data) || (typeof entry.dynamic_form_data === 'object' && entry.dynamic_form_data) || {};
  const primaryName =
    (store?.name && String(store.name).trim()) ||
    (formData.store_name && String(formData.store_name).trim()) ||
    (dynamicData.store_name && String(dynamicData.store_name).trim()) ||
    '';
  const venueFallback =
    (formData[VENUE_FIELD] && String(formData[VENUE_FIELD]).trim()) ||
    (dynamicData[VENUE_FIELD] && String(dynamicData[VENUE_FIELD]).trim()) ||
    '';
  const name = primaryName || venueFallback || null;
  const address = (store?.address && String(store.address).trim()) || null;
  return { name, address };
}

function getVideoRecordingUrl(value) {
  if (!value) return '';
  if (typeof value === 'object' && value.type === 'video_recording' && typeof value.url === 'string') {
    return value.url;
  }
  if (typeof value === 'string' && value.includes('/video-recording/')) {
    return value;
  }
  return '';
}

/** Parsed form_data + dynamic_form_data (same merge order as entry modal / CampaignEntries). */
function getMergedFormResponses(entry) {
  if (!entry) return {};
  const formData =
    parseJsonIfString(entry.form_data) || (typeof entry.form_data === 'object' && entry.form_data) || {};
  const dynamicData =
    parseJsonIfString(entry.dynamic_form_data) ||
    (typeof entry.dynamic_form_data === 'object' && entry.dynamic_form_data) ||
    {};
  return { ...formData, ...dynamicData };
}

/** Serialize one form field for a CSV cell (mirrors CampaignEntries display rules, string-only). */
function formatCsvFormValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  const videoUrl = getVideoRecordingUrl(value);
  if (videoUrl) return videoUrl;
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** field_name → field_label from campaign report (API `formFieldLabels`); backfills legacy `availableFilters.formFields`). */
function buildFormFieldNameToLabelMap(report) {
  const map = new Map();
  if (!report) return map;
  for (const row of report.formFieldLabels || []) {
    if (row.field_name) map.set(row.field_name, row.field_label || row.field_name);
  }
  for (const row of report.availableFilters?.formFields || []) {
    if (row.name && !map.has(row.name)) map.set(row.name, row.label || row.name);
  }
  return map;
}

function humanizeFieldNameKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
}

function CampaignReports() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [entriesSearchInput, setEntriesSearchInput] = useState('');
  const [entriesSearch, setEntriesSearch] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignReport, setCampaignReport] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    productType: ''
  });
  const [availableProductTypes, setAvailableProductTypes] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [stateData, setStateData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [entries, setEntries] = useState([]);
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesTotalPages, setEntriesTotalPages] = useState(1);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  /** Live progress for Analyze: current index vs total OTHER+receipt rows in campaign (from stream). */
  const [analyzeProgress, setAnalyzeProgress] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);
  const [peakTimes, setPeakTimes] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showEntryModal, setShowEntryModal] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE)
      });
      if (search.trim()) params.set('search', search.trim());
      const response = await api.get(`/admin/campaigns?${params.toString()}`);
      setCampaigns(response.data.campaigns);
      setTotal(response.data.total ?? 0);
      setTotalPages(response.data.totalPages ?? 1);
      setError('');
    } catch (err) {
      console.error('Fetch campaigns error:', err);
      setError('Failed to load campaigns');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const applyCampaignSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const fetchCampaignReport = async (campaignId, applyFilters = false) => {
    try {
      setReportLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (applyFilters && filters.startDate) params.append('startDate', filters.startDate);
      if (applyFilters && filters.endDate) params.append('endDate', filters.endDate);
      if (applyFilters && filters.productType) params.append('productType', filters.productType);
      
      
      const response = await api.get(`/admin/reports/campaign/${campaignId}?${params.toString()}`);
      setCampaignReport(response.data);
      setSelectedCampaign(campaigns.find(c => c.id === campaignId));
      setEntriesSearchInput('');
      setEntriesSearch('');
      
      // Set available filters from the response
      if (response.data.availableFilters) {
        if (response.data.availableFilters.productCategories) {
          setAvailableProductTypes(response.data.availableFilters.productCategories);
        }
      }
      
      // Fetch state and product data for pie charts
      fetchStateData(campaignId, params.toString());
      fetchProductData(campaignId, params.toString());
      // Fetch heatmap data
      fetchHeatmapData(campaignId, params.toString());
      // Fetch entries data (explicit '' so request is not stale before entriesSearch state clears)
      fetchEntries(campaignId, 1, '');
      
      setReportLoading(false);
    } catch (err) {
      console.error('Fetch campaign report error:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Could not load report',
        text: 'Failed to load campaign report. Please try again.',
        confirmButtonColor: '#0d9488'
      });
      setReportLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    if (selectedCampaign) {
      fetchCampaignReport(selectedCampaign.id, true);
      fetchEntries(selectedCampaign.id, 1);
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      productType: ''
    });
    if (selectedCampaign) {
      fetchCampaignReport(selectedCampaign.id, false);
    }
  };

  const handleEntryClick = (entry) => {
    setSelectedEntry(entry);
    setShowEntryModal(true);
  };

  const exportToCSV = async () => {
    if (!selectedCampaign) return;
    
    try {
      setExportLoading(true);
      // Fetch all entries without pagination for export
      const params = new URLSearchParams();
      // Don't include page/limit to get all entries
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.productType) params.append('productType', filters.productType);
      if (entriesSearch.trim()) params.append('search', entriesSearch.trim());

      const response = await api.get(`/admin/campaigns/${selectedCampaign.id}/entries?${params.toString()}`);
      const allEntries = response.data.entries || [];

      const dynamicKeySet = new Set();
      for (const entry of allEntries) {
        for (const key of Object.keys(getMergedFormResponses(entry))) {
          if (key === 'phone') continue;
          dynamicKeySet.add(key);
        }
      }
      const dynamicKeys = Array.from(dynamicKeySet).sort();

      const fixedHeaders = [
        'Name',
        'Email',
        'Phone',
        'Entry Method',
        'Selected Product',
        'Actual Product',
        'State',
        'Post Code',
        'Store Name',
        'Store Address'
      ];
      const fieldLabelByName = buildFormFieldNameToLabelMap(campaignReport);
      const dynamicHeaders = dynamicKeys.map(
        (k) => fieldLabelByName.get(k) || humanizeFieldNameKey(k)
      );
      const headers = [...fixedHeaders, ...dynamicHeaders];

      const rows = allEntries.map((entry) => {
        const { name: storeName, address: storeAddress } = getReceiptStoreFromEntry(entry);
        const merged = getMergedFormResponses(entry);
        const baseRow = [
          entry.entry_data?.name || '-',
          entry.entry_data?.email || entry.user?.email || '-',
          merged.phone || entry.entry_data?.phone || entry.user?.phone || '-',
          entry.entry_method || 'Online',
          merged.product_entry_1 || merged.product_entry_2 || merged.product_entry_3 || '-',
          entry.product_name || '-',
          merged.state || '-',
          merged.postcode || '-',
          storeName || '-',
          storeAddress || '-'
        ];
        const dynamicCells = dynamicKeys.map((k) => formatCsvFormValue(merged[k]));
        return [...baseRow, ...dynamicCells];
      });
      
      const quoteCsvCell = (cell) => `"${String(cell).replace(/"/g, '""')}"`;
      const csvContent = [
        headers.map(quoteCsvCell).join(','),
        ...rows.map((row) => row.map(quoteCsvCell).join(','))
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const fileName = `${selectedCampaign.name.replace(/[^a-z0-9]/gi, '_')}_entries_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setExportLoading(false);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Export failed',
        text: 'Could not export CSV. Please try again.',
        confirmButtonColor: '#0d9488'
      });
      setExportLoading(false);
    }
  };

  /** AI receipt extraction for entries where store/venue is OTHER; fills store name from receipt when possible (batched). Uses NDJSON stream so the button can show live `current/totalEligible`. */
  const analyzeOtherVenuesFromReceipts = async () => {
    if (!selectedCampaign) return;
    const base = String(api.defaults.baseURL || '').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    setAnalyzeLoading(true);
    setAnalyzeProgress({ current: 0, totalEligible: null, batchTotal: null });
    let finalPayload = null;
    try {
      const res = await fetch(
        `${base}/admin/campaigns/${selectedCampaign.id}/entries/analyze-receipt-venues`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ limit: 100, stream: true })
        }
      );
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.error || errBody.details || `Request failed (${res.status})`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          let msg;
          try {
            msg = JSON.parse(line);
          } catch {
            continue;
          }
          if (msg.type === 'start') {
            setAnalyzeProgress({
              current: 0,
              totalEligible: msg.totalEligible,
              batchTotal: msg.batchTotal
            });
          } else if (msg.type === 'progress') {
            setAnalyzeProgress({
              current: msg.current,
              totalEligible: msg.totalEligible,
              batchTotal: msg.batchTotal
            });
          } else if (msg.type === 'done') {
            finalPayload = msg;
          } else if (msg.type === 'error') {
            throw new Error(msg.error || 'Analyze failed');
          }
        }
      }
      if (!finalPayload) {
        await Swal.fire({
          icon: 'warning',
          title: 'No summary received',
          text: 'Analyze finished but the server did not return a summary. Check the network tab or try again.',
          confirmButtonColor: '#0d9488'
        });
      } else {
        const {
          updated = 0,
          failed = 0,
          skipped = 0,
          candidateCount = 0,
          limit: batchLimit = 100,
          totalEligible: totalElig = 0
        } = finalPayload;
        const lines = [
          `Eligible OTHER + receipt rows (campaign): ${totalElig}.`,
          `This batch: ${candidateCount} (max ${batchLimit} per run).`,
          `Updated: ${updated}`,
          `Failed: ${failed}`,
          `Skipped: ${skipped}`
        ];
        if (candidateCount >= batchLimit && totalElig > candidateCount) {
          lines.push('More rows may remain — click Analyze again to continue.');
        }
        const summaryIcon =
          failed > 0 && updated === 0 ? 'warning' : updated > 0 ? 'success' : 'info';
        await Swal.fire({
          title: 'Analyze complete',
          icon: summaryIcon,
          html: `<ul class="text-left text-sm text-gray-700 list-disc pl-5 space-y-1 m-0">${lines
            .map((line) => `<li>${line}</li>`)
            .join('')}</ul>`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#0d9488',
          width: '28rem'
        });
      }
      await fetchEntries(selectedCampaign.id, entriesPage);
    } catch (err) {
      console.error('Analyze receipt venues error:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Analyze failed',
        text: err.message || 'Something went wrong. Please try again.',
        confirmButtonColor: '#0d9488'
      });
    } finally {
      setAnalyzeLoading(false);
      setAnalyzeProgress(null);
    }
  };

  const fetchStateData = async (campaignId, params) => {
    try {
      const response = await api.get(`/admin/reports/campaign/${campaignId}/states?${params}`);
      const stateStats = response.data.stateStats || {};
      const chartData = Object.entries(stateStats).map(([state, count]) => ({
        name: state,
        value: count
      }));
      setStateData(chartData);
    } catch (err) {
      console.error('Failed to fetch state data:', err);
      setStateData([]);
    }
  };

  const fetchProductData = async (campaignId, params) => {
    try {
      const response = await api.get(`/admin/reports/campaign/${campaignId}/products?${params}`);
      const productStats = response.data.productStats || {};
      const chartData = Object.entries(productStats).map(([product, count]) => ({
        product: product,
        count: count
      }));
      setProductData(chartData);
    } catch (err) {
      console.error('Failed to fetch product data:', err);
      setProductData([]);
    }
  };

  const fetchHeatmapData = async (campaignId, params) => {
    try {
      const response = await api.get(`/admin/reports/campaign/${campaignId}/heatmap?${params}`);
      setHeatmapData(response.data.heatmapData);
      setPeakTimes(response.data.peakTimes || []);
    } catch (err) {
      console.error('Failed to fetch heatmap data:', err);
      setHeatmapData(null);
      setPeakTimes([]);
    }
  };

  const fetchEntries = async (campaignId, page = 1, searchForRequest) => {
    try {
      setEntriesLoading(true);
      const appliedSearch = searchForRequest !== undefined ? searchForRequest : entriesSearch;
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 10);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.productType) params.append('productType', filters.productType);
      if (String(appliedSearch).trim()) params.append('search', String(appliedSearch).trim());

      const response = await api.get(`/admin/campaigns/${campaignId}/entries?${params.toString()}`);
      setEntries(response.data.entries);
      setEntriesTotalPages(response.data.pagination?.totalPages || 1);
      setEntriesPage(response.data.pagination?.page || page);
      setEntriesLoading(false);
    } catch (err) {
      console.error('Failed to fetch entries:', err);
      setEntriesLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, className = '', icon, trend }) => (
    <div className={`bg-white border border-gray-200 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          {icon && (
            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
              {icon}
            </div>
          )}
          <div>
            <h4 className="text-sm font-medium text-gray-600 uppercase tracking-wide">{title}</h4>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {trend && (
          <div className={`text-xs font-semibold px-2 py-1 rounded-full ${
            trend > 0 ? 'bg-green-100 text-green-800' : 
            trend < 0 ? 'bg-red-100 text-red-800' : 
            'bg-gray-100 text-gray-800'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </div>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
    </div>
  );

  // Pie chart colors
  const COLORS = [
    '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', 
    '#06B6D4', '#F97316', '#84CC16', '#EC4899', '#6366F1',
    '#14B8A6', '#FBBF24', '#A78BFA', '#34D399', '#60A5FA'
  ];

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg">
          <p className="text-sm font-semibold">{payload[0].name}</p>
          <p className="text-xs">Count: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <p className="text-white/60">Loading campaigns...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-900/20 border border-red-500/50 text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="REPORTING_CAMPAIGNS">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-4xl font-bold text-white">REPORTING</h1>
        </div>

        {!selectedCampaign ? (
          // Campaign List View
          <div>
            {/* Search bar */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Search campaigns</label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') e.preventDefault();
                        }}
                        placeholder="Search by name..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={applyCampaignSearch}
                      className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      Search
                    </button>
                  </div>
                </div>
                <div className="flex items-end">
                  {search ? (
                    <button
                      type="button"
                      onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      Clear filters
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                {total === 0
                  ? 'No campaigns'
                  : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total} campaigns`}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-teal-800 text-white">
                      <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                        Campaign Name
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                        Start Date
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                        End Date
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                        Total Entries
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                  {campaigns.map((campaign) => {
                    const isActive = campaign.status === 'active' && 
                      new Date(campaign.start_date) <= new Date() && 
                      new Date(campaign.end_date) >= new Date();
                    
                    return (
                      <tr 
                        key={campaign.id} 
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => fetchCampaignReport(campaign.id)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {(campaign.mechanics_type || 'Standard').replace(/_/g, ' ')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                            isActive
                              ? 'bg-teal-100 text-teal-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(campaign.start_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(campaign.end_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                          <span className="font-medium">{campaign.entry_count || 0}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchCampaignReport(campaign.id);
                            }}
                            className="text-teal-600 hover:text-teal-900"
                            title="View Report"
                          >
                            <BarChart3 className="w-5 h-5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && campaigns.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500 text-sm">
                        {search ? 'No campaigns match your search.' : 'No campaigns found.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              
              {/* Footer with pagination */}
              <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  {total === 0 ? '' : `${total} campaign${total !== 1 ? 's' : ''} total`}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || loading}
                    className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous page"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-medium text-gray-700 min-w-[4rem] text-center">
                    Page {page} / {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages || loading}
                    className="p-2 text-gray-600 hover:text-teal-700 rounded-full hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next page"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Campaign Detail Report View
          <div>
            <div className="flex items-center mb-6">
              <button
                onClick={() => {
                  setSelectedCampaign(null);
                  setCampaignReport(null);
                  setFilters({ startDate: '', endDate: '', productType: '' });
                }}
                className="mr-4 text-white/60 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">{selectedCampaign.name} Report</h2>
                <p className="text-white/60 text-sm">
                  {new Date(selectedCampaign.start_date).toLocaleDateString()} - {new Date(selectedCampaign.end_date).toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={() => navigate(`/admin/campaigns/${selectedCampaign.id}/entries`)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-lg transition-colors"
              >
                View Entries
              </button>
            </div>

            {/* Filters Section */}
            <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-lg mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    placeholder="mm/dd/yyyy"
                    className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    placeholder="mm/dd/yyyy"
                    className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all"
                  />
                </div>
                {availableProductTypes.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Type</label>
                    <select
                      value={filters.productType}
                      onChange={(e) => handleFilterChange('productType', e.target.value)}
                      className="px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500 transition-all appearance-none cursor-pointer"
                    >
                      <option value="">All Products</option>
                      {availableProductTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button
                  onClick={applyFilters}
                  disabled={reportLoading}
                  className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-800 disabled:opacity-50 text-white font-medium rounded-lg transition-all hover:shadow-lg"
                >
                  Apply
                </button>
                <button
                  onClick={clearFilters}
                  disabled={reportLoading}
                  className="px-5 py-2.5 bg-transparent hover:bg-gray-50 disabled:bg-transparent disabled:opacity-50 border border-gray-300 text-gray-700 font-medium rounded-lg transition-all"
                >
                  Clear
                </button>
              </div>
            </div>

            {reportLoading ? (
              <div className="flex justify-center items-center h-64">
                <p className="text-white/60">Loading report...</p>
              </div>
            ) : campaignReport && (
              <div className="space-y-6">
                {/* Overview Stats */}
                <section>
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Campaign Overview</h3>
                    <p className="text-white/70 text-sm">Key metrics and performance indicators</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard
                      title="Total Entries"
                      value={campaignReport.entries.total}
                      subtitle="All submissions"
                      icon={
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      }
                    />
                    <StatCard
                      title="Approved"
                      value={campaignReport.entries.approved}
                      subtitle="Verified entries"
                      className="border-l-4 border-green-500"
                      icon={
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      }
                    />
                    <StatCard
                      title="Pending"
                      value={campaignReport.entries.pending}
                      subtitle="Under review"
                      className="border-l-4 border-yellow-500"
                      icon={
                        <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      }
                    />
                    <StatCard
                      title="Rejected"
                      value={campaignReport.entries.rejected}
                      subtitle="Invalid entries"
                      className="border-l-4 border-red-500"
                      icon={
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      }
                    />
                  </div>
                </section>

                {/* Pie Charts Section */}
                <section className="mt-8">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* State Distribution Pie Chart */}
                    <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-lg">
                      <div className="mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Entries by State</h3>
                        <p className="text-gray-500 text-sm">Geographic distribution of campaign entries</p>
                      </div>
                      
                      {stateData.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Chart Section */}
                          <div className="flex items-center justify-center">
                            <div className="w-full max-w-sm">
                              <ResponsiveContainer width="100%" height={350}>
                                <PieChart>
                                  <Pie
                                    data={stateData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100}
                                    innerRadius={20}
                                    fill="#8884d8"
                                    dataKey="value"
                                    stroke="#fff"
                                    strokeWidth={2}
                                  >
                                    {stateData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                          
                          {/* Legend Section */}
                          <div className="flex items-center">
                            <div className="w-full">
                              <div className="grid grid-cols-1 gap-3">
                                {stateData.map((entry, index) => {
                                  const color = COLORS[index % COLORS.length];
                                  
                                  return (
                                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                      <div 
                                        className="w-5 h-5 rounded-full mr-4 flex-shrink-0 shadow-sm" 
                                        style={{ backgroundColor: color }}
                                      ></div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-gray-900">
                                          {entry.name}
                                        </div>
                                      </div>
                                      <div className="text-sm font-bold text-gray-700 ml-2">
                                        {entry.value}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="h-[400px] flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <BarChart3 className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg">No state data available</p>
                            <p className="text-gray-400 text-sm mt-1">State distribution will appear here once submissions are received</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Product Distribution Pie Chart */}
                    <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-lg">
                      <div className="mb-8">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Product Entries</h3>
                        <p className="text-gray-500 text-sm">Distribution of entries by product type</p>
                      </div>
                      
                      {productData.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          {/* Chart Section */}
                          <div className="flex items-center justify-center">
                            <div className="w-full max-w-sm">
                              <ResponsiveContainer width="100%" height={350}>
                                <PieChart>
                                  <Pie
                                    data={productData}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                                    outerRadius={100}
                                    innerRadius={20}
                                    fill="#8884d8"
                                    dataKey="count"
                                    stroke="#fff"
                                    strokeWidth={2}
                                  >
                                    {productData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                          
                          {/* Legend Section */}
                          <div className="flex items-center">
                            <div className="w-full">
                              <div className="grid grid-cols-1 gap-3">
                                {productData.map((entry, index) => {
                                  const productName = entry.product || entry.name || 'Unknown Product';
                                  const parts = productName.split(' - ');
                                  const color = COLORS[index % COLORS.length];
                                  
                                  return (
                                    <div key={index} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                      <div 
                                        className="w-5 h-5 rounded-full mr-4 flex-shrink-0 shadow-sm" 
                                        style={{ backgroundColor: color }}
                                      ></div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-gray-900 truncate">
                                          {parts.length > 1 ? parts[0] : productName}
                                        </div>
                                        {parts.length > 1 && (
                                          <div className="text-xs text-gray-500 truncate">
                                            {parts[1]}
                                          </div>
                                        )}
                                      </div>
                                      <div className="text-sm font-bold text-gray-700 ml-2">
                                        {entry.count}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="h-[400px] flex items-center justify-center">
                          <div className="text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <BarChart3 className="w-8 h-8 text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg">No product data available</p>
                            <p className="text-gray-400 text-sm mt-1">Product entries will appear here once submissions are received</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </section>

                {/* Submission Heatmap */}
                <section className="mt-8">
                  <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg">
                    <h3 className="text-xl font-bold mb-4 text-gray-900 flex items-center">
                      <svg className="w-6 h-6 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      Submission Activity Heatmap
                    </h3>
                    
                    {heatmapData ? (
                      <div className="overflow-x-auto">
                        <div className="min-w-[1000px]">
                          {/* Prepare data for heatmap */}
                          {(() => {
                            const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                            const hours = Array.from({length: 24}, (_, i) => 
                              i === 0 ? '12AM' : i < 12 ? `${i}AM` : i === 12 ? '12PM' : `${i-12}PM`
                            );
                            
                            // Convert our data format to the library's format
                            const value = [];
                            days.forEach((day, dayIndex) => {
                              const dayData = heatmapData[day] || Array(24).fill(0);
                              dayData.forEach((count, hour) => {
                                if (count > 0) {
                                  value.push({
                                    date: `${dayIndex}-${hour}`,
                                    count: count,
                                    content: `${day} ${hours[hour]}: ${count} submissions`
                                  });
                                }
                              });
                            });
                            
                            return (
                              <div>
                                <div className="mb-2 flex">
                                  <div className="w-24"></div>
                                  {hours.map((hour, i) => (
                                    <div key={i} className="flex-1 text-xs text-gray-600 text-center px-1">
                                      {hour}
                                    </div>
                                  ))}
                                </div>
                                
                                {days.map((day, dayIndex) => {
                                  const dayData = heatmapData[day] || Array(24).fill(0);
                                  const maxValue = Math.max(...Object.values(heatmapData).flat(), 1);
                                  
                                  return (
                                    <div key={day} className="flex mb-1">
                                      <div className="w-24 text-sm text-gray-700 pr-2 py-2 text-right">{day}</div>
                                      <div className="flex flex-1 gap-1">
                                        {dayData.map((value, hour) => {
                                          const intensity = value > 0 ? value / maxValue : 0;
                                          return (
                                            <div
                                              key={hour}
                                              className="relative group flex-1"
                                            >
                                              <div
                                                className="h-8 rounded cursor-pointer transition-all hover:scale-110 hover:shadow-md"
                                                style={{
                                                  backgroundColor: value === 0 
                                                    ? '#f3f4f6' 
                                                    : `rgba(16, 185, 129, ${0.2 + intensity * 0.8})`,
                                                  border: '1px solid #e5e7eb'
                                                }}
                                              />
                                              {value > 0 && (
                                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                                  <span className="text-xs font-medium" style={{
                                                    color: intensity > 0.5 ? '#fff' : '#374151'
                                                  }}>{value}</span>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                                
                                {/* Legend */}
                                <div className="mt-4 flex items-center justify-center">
                                  <div className="flex items-center space-x-4">
                                    <span className="text-sm text-gray-600">Less</span>
                                    <div className="flex space-x-1">
                                      <div className="w-4 h-4 rounded bg-gray-100 border border-gray-300"></div>
                                      <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.3)' }}></div>
                                      <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.5)' }}></div>
                                      <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 0.7)' }}></div>
                                      <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(16, 185, 129, 1)' }}></div>
                                    </div>
                                    <span className="text-sm text-gray-600">More</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                        
                        {/* Peak Times */}
                        {peakTimes.length > 0 && (
                          <div className="mt-6 border-t pt-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-3">Peak Submission Times</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {peakTimes.slice(0, 6).map((peak, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{peak.day}</div>
                                    <div className="text-xs text-gray-600">{peak.hourFormatted}</div>
                                  </div>
                                  <div className="text-lg font-bold text-teal-600">{peak.submissions}</div>
                                </div>
                              ))}
                            </div>
                            
                            {/* Peak Submission Day */}
                            <div className="mt-6">
                              <h4 className="text-sm font-semibold text-gray-700 mb-3">Peak Submission Day</h4>
                              <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-2xl font-bold text-white">{peakTimes[0].day}</p>
                                    <p className="text-teal-100 text-sm mt-1">
                                      {peakTimes[0].hourFormatted} • {peakTimes[0].submissions} submissions
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-white/90 text-sm">Highest activity</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center">
                        <p className="text-gray-400">No submission data available</p>
                      </div>
                    )}
                  </div>
                </section>

                {/* Entries Table */}
                <section className="mt-8">
                  <div className="bg-white border border-gray-200 p-6 rounded-2xl shadow-lg">
                    <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Entry Details</h3>
                      <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end sm:min-w-0">
                        <input
                          type="text"
                          value={entriesSearchInput}
                          onChange={(e) => setEntriesSearchInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.preventDefault();
                          }}
                          placeholder="Name, email, store…"
                          aria-label="Search entries"
                          className="min-w-[12rem] flex-1 sm:flex-initial sm:w-56 h-10 rounded-lg border border-gray-300 px-3 text-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const q = entriesSearchInput.trim();
                            setEntriesSearch(q);
                            fetchEntries(selectedCampaign.id, 1, q);
                          }}
                          className={entryToolbarPrimaryClass}
                        >
                          <Search className="h-4 w-4 shrink-0" aria-hidden />
                          Search
                        </button>
                        {entriesSearch ? (
                          <button
                            type="button"
                            onClick={() => {
                              setEntriesSearchInput('');
                              setEntriesSearch('');
                              fetchEntries(selectedCampaign.id, 1, '');
                            }}
                            className={entryToolbarSecondaryClass}
                          >
                            Clear
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => analyzeOtherVenuesFromReceipts()}
                          disabled={analyzeLoading || exportLoading || !selectedCampaign}
                          title={
                            analyzeProgress?.totalEligible != null
                              ? `Processing batch of ${analyzeProgress.batchTotal ?? '—'} (OTHER + receipt). ${analyzeProgress.totalEligible} eligible in campaign.`
                              : 'For entries with store/venue set to OTHER and a receipt attached, run AI extraction to fill the real store name. Up to 100 entries per run — click again if needed.'
                          }
                          className={`${entryToolbarSecondaryClass} border-violet-200 bg-violet-50 text-violet-900 hover:bg-violet-100 disabled:opacity-50 min-w-[7.5rem]`}
                        >
                          {analyzeLoading ? (
                            <>
                              <svg className="h-4 w-4 shrink-0 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="tabular-nums font-medium">
                                {analyzeProgress?.totalEligible != null
                                  ? `${analyzeProgress.current}/${analyzeProgress.totalEligible}`
                                  : '…'}
                              </span>
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 shrink-0" aria-hidden />
                              Analyze
                            </>
                          )}
                        </button>
                        {entries.length > 0 && (
                          <button
                            type="button"
                            onClick={() => exportToCSV()}
                            disabled={exportLoading || analyzeLoading}
                            className={entryToolbarPrimaryClass}
                          >
                            {exportLoading ? (
                              <>
                                <svg className="h-4 w-4 shrink-0 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Exporting...
                              </>
                            ) : (
                              <>
                                <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                </svg>
                                Export CSV
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {entriesLoading ? (
                      <div className="flex justify-center items-center h-32">
                        <p className="text-gray-400">Loading entries...</p>
                      </div>
                    ) : entries.length > 0 ? (
                      <>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Name / Email
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Entry Method
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Selected Product
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Actual Product
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  State / Post Code
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Store Name
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {entries.map((entry) => (
                                <tr 
                                  key={entry.id} 
                                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                                  onClick={() => handleEntryClick(entry)}
                                >
                                  <td className="px-6 py-4 text-sm text-gray-900">
                                    <div>{entry.entry_data?.name || '-'}</div>
                                    <div className="text-xs text-gray-500">{entry.entry_data?.email || entry.user?.email || '-'}</div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.entry_method || 'Online'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.form_data?.product_entry_1 || 
                                     entry.form_data?.product_entry_2 || 
                                     entry.form_data?.product_entry_3 || 
                                     '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.validated_product_ids && entry.validated_product_ids.length > 0 ? (
                                      entry.validated_product_ids.map(productId => {
                                        const product = campaignReport?.products?.find(p => p.id === productId);
                                        return product ? product.name : `Product ID: ${productId}`;
                                      }).join(', ')
                                    ) : (
                                      entry.product_name || '-'
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {entry.form_data?.state || entry.dynamic_form_data?.state || '-'} / {entry.form_data?.postcode || entry.dynamic_form_data?.postcode || '-'}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                                    {(() => {
                                      const { name: storeName, address: storeAddress } = getReceiptStoreFromEntry(entry);
                                      if (!storeName && !storeAddress) return '-';
                                      return (
                                        <>
                                          <div className="font-medium text-gray-900">{storeName || '-'}</div>
                                          {storeAddress ? (
                                            <div className="text-xs text-gray-500 mt-0.5 leading-snug">{storeAddress}</div>
                                          ) : null}
                                        </>
                                      );
                                    })()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Footer with Pagination */}
                        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
                          {entriesTotalPages > 1 && (
                            <>
                              <div>
                                <p className="text-sm text-gray-700">
                                  Page <span className="font-medium">{entriesPage}</span> of{' '}
                                  <span className="font-medium">{entriesTotalPages}</span>
                                </p>
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => fetchEntries(selectedCampaign.id, entriesPage - 1)}
                                  disabled={entriesPage === 1}
                                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                  Previous
                                </button>
                                <button
                                  onClick={() => fetchEntries(selectedCampaign.id, entriesPage + 1)}
                                  disabled={entriesPage === entriesTotalPages}
                                  className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                >
                                  Next
                                </button>
                              </div>
                            </>
                          )}
                          <div className="flex items-center space-x-4 ml-auto">
                            <span className="text-sm font-medium text-gray-700">
                              {entries.length} entries shown
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <p className="text-sm text-gray-500">No entries found</p>
                      </div>
                    )}
                  </div>
                </section>

              </div>
            )}
          </div>
        )}
      </div>

      {/* Entry Details Modal */}
      {showEntryModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-700 to-teal-800 px-6 py-4">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-white">Entry Details</h3>
                <button
                  onClick={() => {
                    setShowEntryModal(false);
                    setSelectedEntry(null);
                  }}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <div className="p-6">
                {/* Entry Information */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                    Entry Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Entry Number</p>
                        <p className="font-medium text-gray-900">#{selectedEntry.entry_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedEntry.status === 'approved' ? 'bg-green-100 text-green-800' :
                          selectedEntry.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {(selectedEntry.status || 'Pending').charAt(0).toUpperCase() + (selectedEntry.status || 'Pending').slice(1).toLowerCase()}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Submission Date</p>
                        <p className="font-medium text-gray-900">{new Date(selectedEntry.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Entry Method</p>
                        <p className="font-medium text-gray-900">{selectedEntry.entry_method || 'Online'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* User Information */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    User Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Full Name</p>
                        <p className="font-medium text-gray-900">{selectedEntry.entry_data?.name || '-'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-medium text-gray-900">{selectedEntry.entry_data?.email || selectedEntry.user?.email || '-'}</p>
                      </div>
                    </div>
                    {(selectedEntry.dynamic_form_data?.phone || selectedEntry.entry_data?.phone) && (
                      <div>
                        <p className="text-sm text-gray-600">Phone</p>
                        <p className="font-medium text-gray-900">{selectedEntry.dynamic_form_data?.phone || selectedEntry.entry_data?.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Information */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    Product Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Selected Product</p>
                        <p className="font-medium text-gray-900">
                          {selectedEntry.form_data?.product_entry_1 || 
                           selectedEntry.form_data?.product_entry_2 || 
                           selectedEntry.form_data?.product_entry_3 || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Actual Product (from receipt)</p>
                        <p className="font-medium text-gray-900">
                          {selectedEntry.validated_product_ids && selectedEntry.validated_product_ids.length > 0 ? (
                            selectedEntry.validated_product_ids.map(productId => {
                              const product = campaignReport?.products?.find(p => p.id === productId);
                              return product ? product.name : `Product ID: ${productId}`;
                            }).join(', ')
                          ) : (
                            selectedEntry.product_name || '-'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Location Information
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">State</p>
                        <p className="font-medium text-gray-900">
                          {selectedEntry.form_data?.state || selectedEntry.dynamic_form_data?.state || '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Post Code</p>
                        <p className="font-medium text-gray-900">
                          {selectedEntry.form_data?.postcode || selectedEntry.dynamic_form_data?.postcode || '-'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Store Name</p>
                      {(() => {
                        const { name: storeName, address: storeAddress } = getReceiptStoreFromEntry(selectedEntry);
                        return (
                          <>
                            <p className="font-medium text-gray-900">{storeName || '-'}</p>
                            {storeAddress ? (
                              <p className="text-xs text-gray-500 mt-1">{storeAddress}</p>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* Receipt Information */}
                {selectedEntry.receipt_url && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Receipt Information
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Receipt Status</p>
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                          selectedEntry.receipt_status === 'valid' ? 'bg-green-100 text-green-800' :
                          selectedEntry.receipt_status === 'invalid' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {selectedEntry.receipt_status || 'Pending Review'}
                        </span>
                      </div>
                      
                      <a 
                        href={selectedEntry.receipt_url.startsWith('http') ? 
                          selectedEntry.receipt_url : 
                          `${process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || ''}${selectedEntry.receipt_url}`}
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        <Image className="w-5 h-5 mr-2" />
                        View Receipt
                      </a>
                      
                      {selectedEntry.receipt_metadata && (
                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                          {selectedEntry.receipt_metadata.date && (
                            <div>
                              <p className="text-sm text-gray-600">Purchase Date</p>
                              <p className="font-medium text-gray-900">
                                {new Date(selectedEntry.receipt_metadata.date).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          {selectedEntry.receipt_metadata.transactionNumber && (
                            <div>
                              <p className="text-sm text-gray-600">Transaction Number</p>
                              <p className="font-medium text-gray-900">{selectedEntry.receipt_metadata.transactionNumber}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Form Data */}
                {(selectedEntry.form_data || selectedEntry.dynamic_form_data) && (
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-teal-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      All Form Data
                    </h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-2">
                        {Object.entries({...selectedEntry.form_data, ...selectedEntry.dynamic_form_data}).map(([key, value]) => {
                          if (!value || value === '') return null;
                          return (
                            <div key={key} className="flex justify-between items-start">
                              <span className="text-sm text-gray-600">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                              <span className="text-sm font-medium text-gray-900 ml-4 text-right">
                                {Array.isArray(value) ? value.join(', ') : String(value)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default CampaignReports;